/**
 * PMNotificationCenter - PM-Specific Notifications
 *
 * Reflective notifications only - confirms actions, warns about problems.
 * Notifications deep-link back to Pipeline cards, not separate UI.
 */

import { useState, useEffect, useCallback } from "react";

// Notification type configuration
const NOTIFICATION_TYPES = {
  wo_generated: {
    icon: "📋",
    label: "Work Order Generated",
    color: "text-blue-600",
  },
  wo_synced: {
    icon: "🔄",
    label: "Work Order Synced",
    color: "text-blue-600",
  },
  wo_published: {
    icon: "🚀",
    label: "Published to Production",
    color: "text-green-600",
  },
  wo_sync_failed: {
    icon: "⚠️",
    label: "Sync Failed",
    color: "text-red-600",
  },
  wo_validation_error: {
    icon: "🚫",
    label: "Validation Errors",
    color: "text-red-600",
  },
  order_converted: {
    icon: "✅",
    label: "Order Created",
    color: "text-green-600",
  },
  production_complete: {
    icon: "🏁",
    label: "Production Complete",
    color: "text-purple-600",
  },
  closeout_ready: {
    icon: "📝",
    label: "Ready for Close-Out",
    color: "text-purple-600",
  },
  // Cross-department production signals
  proof_ready: {
    icon: "🎨",
    label: "PDF Proofs Ready",
    color: "text-purple-600",
  },
  proof_approved: {
    icon: "✅",
    label: "Proofs Approved",
    color: "text-green-600",
  },
  finals_printed: {
    icon: "🖨️",
    label: "Finals Printed",
    color: "text-blue-600",
  },
  reprint_requested: {
    icon: "🔄",
    label: "Reprint Requested",
    color: "text-amber-600",
  },
};

// Format relative time
function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function PMNotificationCenter({
  acquireAccessToken,
  onUnreadCountChange,
}) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all, unread

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setError("Authentication required");
        return;
      }

      const res = await fetch("/api/pm-center/notifications?limit=100", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        throw new Error("Failed to load notifications");
      }

      const data = await res.json();
      setNotifications(data.notifications || []);
      onUnreadCountChange?.(data.unreadCount || 0);
      setError(null);
    } catch (err) {
      console.error("Error loading notifications:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken, onUnreadCountChange]);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      await fetch(`/api/pm-center/notifications/${notificationId}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
        )
      );

      // Update unread count
      const newUnread = notifications.filter(
        (n) => !n.read_at && n.id !== notificationId
      ).length;
      onUnreadCountChange?.(newUnread);
    } catch (err) {
      console.error("Error marking notification read:", err);
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      await fetch(`/api/pm-center/notifications/${notificationId}/dismiss`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Remove from local state
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (err) {
      console.error("Error dismissing notification:", err);
    }
  };

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = (notification) => {
    if (!notification.read_at) {
      markAsRead(notification.id);
    }

    // Navigate if action URL provided
    if (notification.action_url) {
      // For now, just log - in production would navigate
      console.log("Navigate to:", notification.action_url);
    }
  };

  // Filter notifications
  const filteredNotifications =
    filter === "unread"
      ? notifications.filter((n) => !n.read_at)
      : notifications;

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  if (loading && notifications.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="font-semibold text-gray-900">Notifications</h2>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                {unreadCount} unread
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All</option>
              <option value="unread">Unread only</option>
            </select>

            {/* Refresh */}
            <button
              onClick={loadNotifications}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <svg
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="p-4 bg-red-50 text-red-600 text-sm border-b border-red-100">
            Error: {error}
          </div>
        )}

        {filteredNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-4">🔔</div>
            <p>
              {filter === "unread"
                ? "No unread notifications"
                : "No notifications yet"}
            </p>
            <p className="text-sm mt-2">
              Notifications appear when work orders are published or need attention.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredNotifications.map((notification) => {
              const typeConfig = NOTIFICATION_TYPES[notification.type] || {
                icon: "📬",
                label: notification.type,
                color: "text-gray-600",
              };

              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors
                    ${!notification.read_at ? "bg-blue-50/50" : ""}
                  `}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="text-2xl flex-shrink-0">{typeConfig.icon}</div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p
                            className={`font-medium ${
                              !notification.read_at
                                ? "text-gray-900"
                                : "text-gray-700"
                            }`}
                          >
                            {notification.title}
                          </p>
                          {notification.body && (
                            <p className="text-sm text-gray-600 mt-0.5">
                              {notification.body}
                            </p>
                          )}
                        </div>

                        {/* Dismiss button */}
                        <button
                          onClick={(e) => dismissNotification(notification.id, e)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Dismiss"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Meta row */}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                        <span className={typeConfig.color}>{typeConfig.label}</span>

                        {notification.quote_number && (
                          <span className="font-mono">
                            #{notification.quote_number}
                          </span>
                        )}

                        <span>{formatRelativeTime(notification.created_at)}</span>

                        {!notification.read_at && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>

                      {/* Action link */}
                      {notification.action_label && (
                        <div className="mt-2">
                          <span className="text-sm text-purple-600 hover:underline">
                            {notification.action_label} →
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
